import { UseFormReturn } from "react-hook-form";
import { MessagingModeRadio } from "@/components/dashboard_campaign/settings/MessagingModeRadio";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
  SwitchCard,
  SwitchCardSwitch,
  SwitchCardContent,
  SwitchCardLabel,
  SwitchCardDescription,
} from "@/components/ui/switch-card";
import {
  RadioGroup,
  RadioCard,
  RadioCardItem,
  RadioCardContent,
  RadioCardDescription,
} from "@/components/ui/radio-group";
import { CampaignFormData } from "@/components/dashboard_create_campaign/campaignFormTypes";
import { Globe, UserX, ListX, DollarSignIcon, Percent, AlertCircleIcon } from "lucide-react";
import { useUser } from "@clerk/react";
import google from "@/assets/google.svg";
import microsoft from "@/assets/microsoft.svg";
import { Input } from "@/components/ui/input";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

interface CampaignFormStep4Props {
  form: UseFormReturn<CampaignFormData>;
  loading: boolean;
}

export default function CampaignFormStep4({
  form,
  loading,
}: CampaignFormStep4Props) {
  const { user } = useUser();

  const validAccounts = user?.externalAccounts?.filter((account) => {
    const scopes: string = account.approvedScopes || "";
    if (account.provider === "google") {
      return scopes.includes("gmail.send") && scopes.includes("gmail.readonly");
    }
    if (account.provider === "microsoft") {
      return scopes.includes("Mail.Read") && scopes.includes("Mail.Send") && scopes.includes("Mail.ReadWrite");
    }
    return false;
  }) ?? [];

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <FormLabel className="font-semibold">Other Settings</FormLabel>
        <FormDescription>
          Exclude past creators, set email language, and enable smart
          negotiation.
        </FormDescription>
        <FormField
          control={form.control}
          name="exclude_past_campaign_creators"
          render={({ field }) => (
            <FormItem>
              <SwitchCard disabled={loading}>
                <FormControl>
                  <SwitchCardSwitch
                    id="exclude-past-campaign-creators"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-describedby="exclude-past-campaign-creators-description"
                  />
                </FormControl>
                <SwitchCardContent>
                  <SwitchCardLabel htmlFor="exclude-past-campaign-creators">
                    <UserX />
                    Exclude Past Campaign Creators
                    <span className="text-muted-foreground text-xs">
                      (Optional)
                    </span>
                  </SwitchCardLabel>
                  <SwitchCardDescription id="exclude-past-campaign-creators-description" className="pl-6">
                    Exclude creators from your previous campaigns from this
                    search.
                  </SwitchCardDescription>
                </SwitchCardContent>
              </SwitchCard>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="exclude_excluded_creators"
          render={({ field }) => (
            <FormItem>
              <SwitchCard disabled={loading}>
                <FormControl>
                  <SwitchCardSwitch
                    id="exclude-excluded-creators"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-describedby="exclude-excluded-creators-description"
                  />
                </FormControl>
                <SwitchCardContent>
                  <SwitchCardLabel htmlFor="exclude-excluded-creators">
                    <ListX />
                    Exclude Creators from Exclusion List
                    <span className="text-muted-foreground text-xs">
                      (Optional)
                    </span>
                  </SwitchCardLabel>
                  <SwitchCardDescription id="exclude-excluded-creators-description" className="pl-6">
                    Exclude creators from your managed exclusion list in
                    settings.
                  </SwitchCardDescription>
                </SwitchCardContent>
              </SwitchCard>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="use_creator_native_language"
          render={({ field }) => (
            <FormItem>
              <SwitchCard disabled={loading}>
                <FormControl>
                  <SwitchCardSwitch
                    id="use-creator-native-language"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    aria-describedby="use-creator-native-language-description"
                  />
                </FormControl>
                <SwitchCardContent>
                  <SwitchCardLabel htmlFor="use-creator-native-language">
                    <Globe />
                    Use Creator's Native Language
                    <span className="text-muted-foreground text-xs">
                      (Optional)
                    </span>
                  </SwitchCardLabel>
                  <SwitchCardDescription id="use-creator-native-language-description" className="pl-6">
                    Enable Amy to email creators in their native language when
                    possible. (EN default)
                  </SwitchCardDescription>
                </SwitchCardContent>
              </SwitchCard>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="enable_smart_negotiation"
          render={({ field }) => (
            <FormItem>
              <SwitchCard disabled={loading}>
                <FormControl>
                  <SwitchCardSwitch
                    id="enable_smart_negotiation"
                    checked={field.value}
                    onCheckedChange={(val) => {
                      field.onChange(val);
                      if (!val) {
                        form.setValue("smart_negotiation_percentage", undefined);
                      }
                    }}
                    aria-describedby="enable-smart-negotiation-description"
                  />
                </FormControl>
                <SwitchCardContent>
                  <SwitchCardLabel htmlFor="enable-smart-negotiation">
                    <DollarSignIcon />
                    Enable Smart Negotiation
                    <span className="text-muted-foreground text-xs">
                      (Optional)
                    </span>
                  </SwitchCardLabel>
                  <SwitchCardDescription id="enable-smart-negotiation-description" className="pl-6">
                    Amy counter-offers even when prices are within budget.
                  </SwitchCardDescription>
                </SwitchCardContent>
              </SwitchCard>

               {field.value && (
                <div className="space-y-2 mt-1 mb-6 ml-3 pl-3 border-l border-dashed">
                  <Label className="flex items-center gap-2">
                    <Percent className="size-4 text-muted-foreground" />
                    Negotiation Percentage
                  </Label>
                  <FormField
                    control={form.control}
                    name="smart_negotiation_percentage"
                    render={({ field: percentField }) => (
                      <FormItem>
                        <FormControl>
                          <div className="flex items-center gap-2 max-w-[100px]">
                            <Input
                              autoFocus
                              onFocus={(e) => e.target.select()}
                              type="text"
                              placeholder="80"
                              disabled={loading}
                              value={percentField.value ?? ""}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                percentField.onChange(value === "" ? undefined : Number(value));
                              }}
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    Amy will negotiate the price down to this percentage
                    of the creator's asking price. Default 80%.
                  </p>
                </div>
              )}



              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="space-y-2">
        <FormLabel className="font-semibold">External Accounts</FormLabel>
        <FormDescription>
          Choose an external account. Its email address will send messages to
          creators.
        </FormDescription>
        <FormField
          control={form.control}
          name="external_account_id"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormControl>
                {validAccounts.length > 0 ? (
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="gap-2 lg:grid-cols-2"
                  >
                    {validAccounts.map((account) => (
                      <RadioCard key={account.id}>
                        <RadioCardContent>
                          <Label
                            htmlFor={account.id}
                            className="flex items-center gap-2"
                          >
                            {account.provider === "google" && (
                              <img src={google} alt="Google" className="size-4" />
                            )}
                            {account.provider === "microsoft" && (
                              <img src={microsoft} alt="Microsoft" className="size-4" />
                            )}
                            <span className="capitalize">{account.provider}</span>
                          </Label>
                          <RadioCardDescription>
                            {account.emailAddress}
                          </RadioCardDescription>
                        </RadioCardContent>
                        <RadioCardItem value={account.id} id={account.id} />
                      </RadioCard>
                    ))}
                  </RadioGroup>
                ) : (
                  <Alert variant="warning">
                    <AlertTitle>
                      <AlertCircleIcon />
                      No valid email account connected
                    </AlertTitle>
                    <AlertDescription>
                      You need to connect at least one Google or Microsoft account with the correct mail permissions before creating a campaign. Go to your account settings to connect one.
                    </AlertDescription>
                  </Alert>
                )}
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="space-y-2">
        <FormLabel className="font-semibold">Messaging</FormLabel>
        <FormDescription>
          Choose whether Amy writes and sends the messages, or just drafts them
          for you to review and send.
        </FormDescription>
        <FormField
          control={form.control}
          name="messaging"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <MessagingModeRadio
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={loading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
